<template>
  <div class="gauge-wrap" :class="{ 'label-top': labelTop }">
  <div v-if="labelTop && detailMode !== 'billet'" class="label">
    <span>{{ label }}</span>
    <slot name="label-extra"></slot>
  </div>

    <div :id="chartId" class="gauge-chart"></div>
  </div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "GaugeChart",
  props: {
    useYn: { type: [String, Boolean], default: "Y" },
    value: { type: [Number, String], default: 0 },
    label: { type: String, default: "" },
    detailMode: { type: String, default: "default" }, 
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },

    unit: { type: String, default: "" },

    thresholdMin: { type: Number, default: 0 },
    thresholdMax: { type: Number, default: 100 },
    labelTop: { type: Boolean, default: false },
    subLabel: { type: [Number, String], default: null },

    bands: {
      type: Array,
      default: () => [
        { to: 40, color: "#8bfd82" },
        { to: 60, color: "#ff9409" },
        { to: 100, color: "#d01919" }
      ]
    },

    showThresholdBar: { type: Boolean, default: true }
  },

  data() {
    return {
      chart: null,
      chartId: "gauge_" + Math.random().toString(36).substr(2, 9)
    };
  },

  mounted() {
    this.initChart();
    window.addEventListener("resize", this.resizeChart);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.updateChart(newVal);
      }
    },

    bands: {
      deep: true,
      handler() {
        this.rebuildChart();
      }
    },
    thresholdMax() {
      this.rebuildChart();
    },
    thresholdMin() {
      this.rebuildChart();
    },
    labelTop() {
      this.rebuildChart();
    },
    label() {
      this.rebuildChart();
    },

    // ✅ useYn 바뀌면 기준치 on/off + 눈금 재생성
    useYn() {
      this.rebuildChart();
    },
    min() {
      this.rebuildChart();
    },
    max() {
      this.rebuildChart();
    }
  },

  computed: {
    isThresholdEnabled() {
      if (typeof this.useYn === "boolean") return this.useYn;
      return String(this.useYn).toUpperCase() === "Y";
    }
  },

  methods: {
    clamp(n, min, max) {
      const v = Number(n);
      if (Number.isNaN(v)) return min;
      return Math.min(max, Math.max(min, v));
    },

    mapValueToThresholdScale(v) {
      const valueClamped = this.clamp(v, this.min, this.max);
      const denom = (this.max - this.min) || 1;
      const ratio = (valueClamped - this.min) / denom; // 0~1
      return this.thresholdMin + ratio * (this.thresholdMax - this.thresholdMin);
    },

    normalizeBands() {
      const min = this.thresholdMin;
      const max = this.thresholdMax;

      const sorted = (this.bands || [])
        .map(b => ({ to: Number(b.to), color: b.color }))
        .filter(b => Number.isFinite(b.to) && typeof b.color === "string")
        .sort((a, b) => a.to - b.to);

      if (sorted.length === 0) {
        return [{ to: max, color: "#999" }];
      }

      if (sorted[sorted.length - 1].to < max) {
        sorted.push({ to: max, color: sorted[sorted.length - 1].color });
      }

      return sorted.map(b => ({
        to: this.clamp(b.to, min, max),
        color: b.color
      }));
    },

    makeDetailFormatter(v) {

      // ✅ billet 모드: "현재값/기준값"
      if (this.detailMode === "billet") {
        const curTxt = `${Number(v).toFixed(1)} ${this.unit}`; // 현재값은 그대로 ℃ 유지

        const setVal = Number(this.bands?.[0]?.to);
        const setTxt = Number.isFinite(setVal) ? `${Math.round(setVal)}` : "-"; // ✅ ℃ 제거

        // ✅ 기준값만 다른 스타일 적용 가능하도록 분리
        return `{cur|${curTxt}}{sep| / }{set|${setTxt}}`;
      }

      const numTxt = `${Number(v).toFixed(1)} `;
      const subTxt = this.subLabel !== null ? `{sub|\nSet : ${Number(this.subLabel).toFixed(1)}}` : "";
      if (this.labelTop) return `{val|${numTxt}}{unt|${this.unit}}${subTxt}`;
      return `{lbl|${this.label}}\n{val|${numTxt}}{unt|${this.unit}}${subTxt}`;
    },

    getThresholdColorStops() {
      const bands = this.normalizeBands();
      const min = this.thresholdMin;
      const max = this.thresholdMax;
      const denom = (max - min) || 1;

      return bands.map(b => {
        const ratio = (b.to - min) / denom;
        return [ratio, b.color];
      });
    },

    getValueColor(val) {
      // useYn = N이면 무조건 초록
      if (!this.isThresholdEnabled) return "#8bfd82";

      const scaled = this.mapValueToThresholdScale(val);
      const bands = this.normalizeBands(); // bands에 들어온 color를 그대로 쓰는 방식

      // ✅ billet 모드는 bands(2개) 그대로 사용 → 빨강이 나올 수가 없음
      if (this.detailMode === "billet") {
        for (let i = 0; i < bands.length; i++) {
          if (scaled <= bands[i].to) return bands[i].color;
        }
        return bands[bands.length - 1]?.color || "#8bfd82";
      }

      // ✅ 일반 센서: 5구간 정책은 "bands가 5개 이상일 때만"
      // (lowDanger, lowWarn, warn, danger, max)
      if (bands.length >= 5) {
        const lowDanger = bands[0].to;
        const lowWarn   = bands[1].to;
        const warn      = bands[2].to;
        const danger    = bands[3].to;

        if (scaled <= lowDanger) return "#d01919";
        if (scaled <= lowWarn)   return "#ffa800";
        if (scaled <= warn)      return "#8bfd82";
        if (scaled <= danger)    return "#ffa800";
        return "#d01919";
      }

      // ✅ 그 외(3개 bands 같은 경우)는 bands 그대로 적용 (기존 방식)
      for (let i = 0; i < bands.length; i++) {
        if (scaled <= bands[i].to) return bands[i].color;
      }
      return bands[bands.length - 1]?.color || "#8bfd82";
    },

    // =========================
    // ✅ 여기부터: "4개만 표시"용 추가 코드
    // =========================
    getKeyTickValues() {
      const vals = [this.min, this.max];

      if (this.isThresholdEnabled) {
        const nb = this.normalizeBands();
        // nb는 lowDanger, lowWarn, warn, danger, (max) 형태를 기대
        // 앞에서 4개까지만 뽑아 경계로 사용
        if (nb[0]?.to != null) vals.push(nb[0].to); // lowDanger
        if (nb[1]?.to != null) vals.push(nb[1].to); // lowWarn
        if (nb[2]?.to != null) vals.push(nb[2].to); // warn
        if (nb[3]?.to != null) vals.push(nb[3].to); // danger
      }

      const uniq = Array.from(new Set(vals.map(v => Number(v)).filter(v => Number.isFinite(v))));
      uniq.sort((a, b) => a - b);
      return uniq;

      // const vals = [];

      // if (this.isThresholdEnabled) {
      //   const nb = this.normalizeBands();

      //   if (nb[0]?.to != null) vals.push(nb[0].to); // warn
      //   if (nb[1]?.to != null) vals.push(nb[1].to); // danger
      // }

      // const uniq = Array.from(new Set(vals.map(v => Number(v)).filter(v => Number.isFinite(v))));
      // uniq.sort((a, b) => a - b);
      // return uniq;
    },

    valueToAngleDeg(v) {
      // min -> 180deg, max -> 0deg
      const denom = (this.max - this.min) || 1;
      const ratio = (v - this.min) / denom; // 0~1
      const clampedRatio = Math.min(1, Math.max(0, ratio));
      return 180 * (1 - clampedRatio);
    },

    buildKeyTickGraphics() {
      if (!this.chart) return [];

      const w = this.chart.getWidth();
      const h = this.chart.getHeight();

      // main gauge 기준 (너 코드 고정값과 동일하게 맞춤)
      const cx = w * 0.5;
      const cy = h * 0.70;

      const base = Math.min(w, h) / 2;
      const r = base * 0.96;          // radius: "96%"
      const tickOuter = r - 2;         // 바깥쪽
      const tickInner = r - (-18);        // 눈금 길이 (적당히)
      const textR = r - (-32);            // 숫자 위치(반원 안쪽으로)

      const ticks = this.getKeyTickValues();

      const children = [];
      ticks.forEach((tv, idx) => {
        const angle = this.valueToAngleDeg(tv);
        const rad = (angle * Math.PI) / 180;

        const x1 = cx + tickInner * Math.cos(rad);
        const y1 = cy - tickInner * Math.sin(rad);
        const x2 = cx + tickOuter * Math.cos(rad);
        const y2 = cy - tickOuter * Math.sin(rad);

        const tx = cx + textR * Math.cos(rad);
        const ty = cy - textR * Math.sin(rad);

        children.push(
          {
            id: `keyTickLine_${idx}`,
            type: "line",
            shape: { x1, y1, x2, y2 },
            style: { stroke: "#000", lineWidth: 1 }
          },
          {
            id: `keyTickText_${idx}`,
            type: "text",
            x: tx,
            y: ty,
            style: {
              text: String(Math.round(tv)), // ✅ 표시 숫자(원하면 toFixed 등으로 바꿔도 됨)
              fill: "#000",
              fontSize: 13,
              fontWeight: 600,
              align: "center",
              verticalAlign: "middle"
            }
          }
        );
      });

      return [
        {
          id: "keyTicksGroup",
          type: "group",
          children
        }
      ];
    },

    updateKeyTickGraphics() {
      if (!this.chart) return;
      this.chart.setOption(
        {
          graphic: this.buildKeyTickGraphics()
        },
        { notMerge: false, lazyUpdate: true }
      );
    },
    // =========================

    initChart() {
      const chartDom = document.getElementById(this.chartId);
      if (!chartDom) return;

      this.chart = echarts.init(chartDom);

      const initColor = this.getValueColor(this.value);

      const series = [
        {
          type: "gauge",
          center: ["50%", "70%"],
          radius: "110%",
          startAngle: 180,
          endAngle: 0,
          min: this.min,
          max: this.max,
          splitNumber: 6, // ✅ 이제 의미 없음(눈금/숫자 숨김)

          itemStyle: { color: initColor },
          progress: {
            show: true,
            width: 17,
            itemStyle: { color: initColor }
          },

          pointer: { show: false },
          axisLine: { lineStyle: { width: 17, color: [[1, "#E6E6E6"]] } },

          // ✅ 기본 눈금/숫자 숨김 (우리가 graphic으로 4개만 그릴거라서)
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          // axisTick: { 
          //   distance: -25, 
          //   splitNumber: 1, 
          //   lineStyle: { 
          //     width: 1, 
          //     color: "#999" 
          //   } 
          // }, 
          // splitLine: { 
          //   show: false, 
          //   distance: -24, 
          //   length: 8, 
          //   lineStyle: { 
          //     width: 2, 
          //     color: "#999" 
          //   } 
          // }, 
          // axisLabel: { 
          //   distance: -7, 
          //   color: "#999", 
          //   fontSize: 10, formatter: (val) => Math.round(val) 
          // },

          anchor: { show: false },
          title: { show: false },

          detail: {
            valueAnimation: true,
            width: "100%",
            lineHeight: 22,
            borderRadius: 8,
            offsetCenter: [0, this.detailMode === "billet" ? "30%" : "-15%"],
            fontSize: 18,
            fontWeight: "bolder",
            formatter: () => this.makeDetailFormatter(this.value),
            rich: {
              lbl: {
                color: "#000",
                fontSize: 14,
                fontWeight: 500,
                padding: [0, 0, 0, 0]
              },
              val: {
                color: "#000",
                fontSize: 24,
                fontWeight: "bolder"
              },
              
              // ✅ billet 전용 (현재값)
              cur: {
                color: "#000",
                fontSize: 24,        // 원하는 크기로
                fontWeight: 800      // 볼드 유지
              },

              // ✅ 슬래시
              sep: {
                color: "#000",
                fontSize: 20,
                fontWeight: 400
              },

              // ✅ billet 전용 (기준값: ℃ 제거 + 볼드 제거 + 작게)
              set: {
                color: "#999",
                fontSize: 18,        // ✅ 더 작게
                fontWeight: 600      // ✅ 볼드 제거
              },

              // ✅ 단위 (mm/s, ℃ 등) — 숫자보다 작게
              unt: {
                color: "#000",
                fontSize: 20,
                fontWeight: "bolder"
              },

              // ✅ subLabel (셋스피드 등)
              sub: {
                color: "#999",
                fontSize: 18,
                fontWeight: 700
              }
            }
          },

          data: [{ value: this.value }]
        }
      ];

      if (this.isThresholdEnabled && this.showThresholdBar) {
        series.push({
          id: "thresholdGauge",
          type: "gauge",
          center: ["50%", "70%"],
          radius: "89%",
          startAngle: 180,
          endAngle: 0,
          min: this.thresholdMin,
          max: this.thresholdMax,

          pointer: { show: false },
          progress: { show: false },

          axisLine: {
            lineStyle: {
              width: 7,
              color: this.getThresholdColorStops()
            }
          },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          detail: { show: false },
          title: { show: false },

          data: [{ value: this.thresholdMin }]
        });
      }

      this.chart.setOption({ series });

      // ✅ 4개만 표시하는 눈금/숫자 그리기
      this.updateKeyTickGraphics();
      this.updateGraphics();
    },

    updateChart(val) {
      if (!this.chart) return;

      const v = this.clamp(val, this.min, this.max);
      const color = this.getValueColor(v);

      this.chart.setOption({
        series: [
          {
            itemStyle: { color },
            progress: { itemStyle: { color } },
            data: [{ value: v }],
            detail: {
              formatter: () => this.makeDetailFormatter(val)
            }
          }
        ]
      });
    },

    rebuildChart() {
      if (!this.chart) return;
      // this.chart.clear();
      this.initChart();
      this.updateChart(this.value);
    },

    resizeChart() {
      if (!this.chart) return;
      this.chart.resize();
      // ✅ 리사이즈되면 graphic 위치도 다시 계산해야 함
      this.updateKeyTickGraphics();
      this.updateGraphics();
    },

    updateGraphics() {
      if (!this.chart) return;

      const g = [
        ...this.buildKeyTickGraphics(),
        ...this.buildInnerLabelGraphics()
      ];

      this.chart.setOption(
        { graphic: g },
        { notMerge: false, lazyUpdate: true }
      );
    },

    buildInnerLabelGraphics() {
      if (!this.chart) return [];

      // ✅ 빌렛만
      if (this.detailMode !== "billet") return [];

      const w = this.chart.getWidth();
      const h = this.chart.getHeight();

      // main gauge 기준 (너 코드 center: ["50%","70%"])
      const cx = w * 0.5;
      const cy = h * 0.70;

      return [{
        id: "innerLabelGroup",
        type: "group",
        children: [
          {
            id: "innerLabelText",
            type: "text",
            x: cx,
            y: cy - 28, // ✅ 위치: 게이지 안쪽 위 (원하면 숫자만 조절)
            style: {
              text: this.label,   // "Zone 1" 같은 기존 label 그대로 사용
              fill: "#000",
              fontSize: 20,
              fontWeight: 700,
              align: "center",
              verticalAlign: "middle"
            }
          }
        ]
      }];
    },

    updateInnerLabelGraphics() {
      if (!this.chart) return;
      this.chart.setOption(
        { graphic: this.buildInnerLabelGraphics() },
        { notMerge: false, lazyUpdate: true }
      );
    },
  }
};
</script>

<style scoped>
.gauge-wrap {
  display: flex;
  /* justify-content: center; */
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.label-out {
  font-size: 13px;
  font-weight: 900;
  text-align: center;
  max-width: 260px;
  color: #222;
  line-height: 1.15;
  margin-bottom: 8px;
  white-space: normal;
  margin-bottom: -24px;
}

.label {
  font-size: 15pt;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* 차트 */
.gauge-chart {
  width: 100%;
  height: 160px;
  display: block;
  margin: 0 auto;
  display: flex;
  justify-content: center;
}
</style>
